import {
  CreateScreenResponse,
  groupEnum,
  KNOWLEDGE_GRAPH_FILTERS,
  MARKET_FILTERS,
  PostFilterBody,
  ScreenerFilter,
  ScreenerFilterGroupKeys,
  TableState,
} from '@api';
import { ActiveFilterOptions } from '@terminal/components/filters/Filters';
import { FilterOption } from '@terminal/components/update-filter-options/updateFilterOptions';
import { WATCHLIST_ID, WORKING_LIST_ID } from '@terminal/config/constants';
import { addFiltersToActiveFilters } from '@terminal/hooks/use-filter-actions/use-filter-actions-utils';
import { FilterGroups } from '@terminal/hooks/use-filter-actions/useFilterActions';
import { useWorkingList } from '@terminal/hooks/use-working-list/useWorkingList';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { getWatchlistEntitiesTags } from '@terminal/stores/use-watchlist/useWatchlist.utils';
import { usePerformanceStore } from '@terminal/views/theme-overview/tab-content/performance/hooks/usePerformanceStore';
import { joinFilterNames } from '@terminal/widgets/scenario/utils/scenario-conditions/scenario-conditions';
import { v4 } from 'uuid';

import { KNOWLEDGE_GRAPH_TAB } from '../results/screener-table-utils';

export const mapFilters = (
  activeFilters: ActiveFilterOptions[]
): PostFilterBody => {
  const requestFromFilters = activeFilters.reduce<PostFilterBody>(
    (res, filter) => {
      if (filter.options[0].childName && filter.options.length === 1) {
        return res;
      }

      if (!res[filter.filter]) {
        res[filter.filter] = { values: [], children: [] };
      }

      if (filter.filter === RANK_FILTERS_KEY) {
        const limitValue = filter.options[0].value.id;
        const limit = typeof limitValue === 'number' ? limitValue : 0;
        const tags = usePerformanceStore
          .getState()
          .companiesTheme.slice(0, limit)
          .map(c => c.company);

        if (tags.length) {
          res = {
            entity_tag: {
              values: [{ tags }],
            },
          };
        }
      } else if (filter.filter === ASSET_FILTERS_KEY) {
        const rawTags = filter.options[0].value?.tags;
        const directTags = Array.isArray(rawTags) ? rawTags : [];
        const isWatchlistFilter = WATCHLIST_ID in filter.options[0].value;
        const isWorkingList = WORKING_LIST_ID in filter.options[0].value;

        if (directTags.length > 0) {
          res.entity_tag.values = [{ tags: directTags }];
        } else if (Array.isArray(rawTags)) {
          delete res.entity_tag;
        } else if (isWatchlistFilter) {
          const lists = useWatchlist.getState().lists;
          const watchlistTags = getWatchlistEntitiesTags(
            lists,
            filter.options[0].value.watchlistId
          );

          if (watchlistTags) {
            res.entity_tag.values = [{ tags: watchlistTags }];
          } else {
            delete res.entity_tag;
          }
        } else if (isWorkingList) {
          const workingList = useWorkingList.getState().workingList;
          res.entity_tag.values = [{ tags: workingList }];
        }
      } else {
        filter.options.forEach(o => {
          if (o.childName) {
            res[filter.filter].children?.push(o.value);
          } else {
            res[filter.filter].values.push(o.value);
          }
        });
      }

      return res;
    },
    {}
  );

  return requestFromFilters;
};

export const reduceSavedScreenFilterGroups = (
  filterGroups: FilterGroups<ScreenerFilterGroupKeys>,
  savedFilterOptionsPerGroup: Record<
    ScreenerFilterGroupKeys,
    ActiveFilterOptions[]
  >
) => {
  let hasInvalidWatchlist = false;

  const reducedSavedScreenFilterGroups: FilterGroups<ScreenerFilterGroupKeys> =
  {
    [MARKET_FILTERS]: { ...filterGroups[MARKET_FILTERS] },
    [KNOWLEDGE_GRAPH_FILTERS]: { ...filterGroups[KNOWLEDGE_GRAPH_FILTERS] },
  };

  groupEnum.options.forEach(key => {
    const activeFilterOptions = savedFilterOptionsPerGroup[key] ?? [];

    const savedWatchlists = useWatchlist.getState().lists;
    const savedWatchlistIds = new Set(savedWatchlists.map(l => l.id));
    const sanitizedActiveFilterOptions = activeFilterOptions
      .map(option => {
        if (option.filter !== ASSET_FILTERS_KEY) {
          return option;
        }

        const sanitizedOptions = option.options.filter(o => {
          const value = o.value;

          if (WATCHLIST_ID in value) {
            const watchlistIdValue = value[WATCHLIST_ID];
            const foundWatchlistId =
              typeof watchlistIdValue === 'string'
                ? watchlistIdValue
                : undefined;
            const hasValidWatchlist =
              !!foundWatchlistId && savedWatchlistIds.has(foundWatchlistId);
            hasInvalidWatchlist = !hasValidWatchlist;
            return hasValidWatchlist;
          }

          return true;
        });

        return {
          ...option,
          options: sanitizedOptions,
        };
      })
      .filter(
        option =>
          option.options.length > 0 || option.filter !== ASSET_FILTERS_KEY
      );

    const { activeFilters, allFilters } = filterGroups[key];

    const mapKeyToFilter: Record<string, ScreenerFilter> = {};
    activeFilters.forEach(item => {
      mapKeyToFilter[item.key] = item;
    });

    const notVisibleByDefault = new Set<string>();
    sanitizedActiveFilterOptions.forEach(f => {
      if (!mapKeyToFilter[f.filter]) {
        notVisibleByDefault.add(f.filter);
      }
    });

    reducedSavedScreenFilterGroups[key] = {
      allFilters,
      activeFilterOptions: sanitizedActiveFilterOptions,
      activeFilters: notVisibleByDefault.size
        ? addFiltersToActiveFilters({
          activeFilters,
          allFilters,
          newFilterKeys: [...notVisibleByDefault],
        })
        : activeFilters,
    };
  });

  return {
    filterGroups: reducedSavedScreenFilterGroups,
    hasInvalidWatchlist,
  };
};

export const getSavedScreenFilterGroups = (
  filterGroups: FilterGroups<ScreenerFilterGroupKeys>,
  {
    filterOptionGroups,
    version,
    activeFilterOptions = [],
  }: CreateScreenResponse['config']
) => {
  if (version === 'v1') {
    if (!filterOptionGroups) {
      return {
        filterGroups,
        hasInvalidWatchlist: false,
      };
    }

    return reduceSavedScreenFilterGroups(filterGroups, filterOptionGroups!);
  }

  const finalFilterOptionGroups: Record<
    ScreenerFilterGroupKeys,
    ActiveFilterOptions[]
  > = {
    [MARKET_FILTERS]: [],
    [KNOWLEDGE_GRAPH_FILTERS]: [],
  };

  activeFilterOptions.forEach(next => {
    const group: ScreenerFilterGroupKeys = [
      'theme_exposure',
      'country_exposure',
    ].includes(next.filter)
      ? KNOWLEDGE_GRAPH_FILTERS
      : MARKET_FILTERS;

    if (group === KNOWLEDGE_GRAPH_FILTERS) {
      next.options.forEach(item => {
        const options = [item];
        const id = createFilterId(next.filter);
        finalFilterOptionGroups[group].push({
          filter: next.filter,
          id,
          options,
        });
      });
    } else {
      finalFilterOptionGroups[group].push({ ...next, id: next.filter });
    }
  });

  return reduceSavedScreenFilterGroups(filterGroups, finalFilterOptionGroups);
};

export const getFilterOptionsPerGroup = (
  filterGroups: FilterGroups<ScreenerFilterGroupKeys>
): Record<ScreenerFilterGroupKeys, ActiveFilterOptions[]> => ({
  [MARKET_FILTERS]: filterGroups[MARKET_FILTERS].activeFilterOptions,
  [KNOWLEDGE_GRAPH_FILTERS]:
    filterGroups[KNOWLEDGE_GRAPH_FILTERS].activeFilterOptions,
});

export const createFilterId = (key: string) => `${key}_${v4()}`;

export const getFiltersNamesList = (
  activeFilters: ScreenerFilter[],
  activeFilterOptions: ActiveFilterOptions[]
): string[] => {
  return activeFilterOptions.map(activeFilterOption => {
    const activeFilter = activeFilters.find(
      activeFilter => activeFilter.key === activeFilterOption.filter
    );

    const filterName = activeFilter?.name;
    const filterValues = joinFilterNames(activeFilterOption);

    return `${filterName}: ${filterValues}`;
  });
};

export const ASSET_FILTERS_KEY = 'entity_tag';
export const RANK_FILTERS_KEY = 'fe_rank';

export const assetFilter: ScreenerFilter = {
  key: ASSET_FILTERS_KEY,
  name: 'Assets',
  visible: true,
  searchable: false,
  category: 'Universe',
  locked: true,
  config: {
    multiselect: false,
    name: 'Assets',
    options: [],
  },
};

// resets table state analyzing changed table state and saved screen table state
// handles case when some of the columns in the changed table state doesn't exist in the saved screen table state
// and in that case uses saved screen table state
// Note: this is called when resetting filter options to saved screen ones,
// meaning some of the columns may not exist for the saved screen filter options
export const resetKGTableState = (
  tableState: TableState,
  savedScreenTableState: TableState
): TableState => {
  const sortingMatches = tableState.sorting.every(item =>
    savedScreenTableState.sorting.some(s => s.id === item.id)
  );

  const pinnedMatches = tableState.pinnedColumns[KNOWLEDGE_GRAPH_TAB]?.every(
    id => savedScreenTableState.pinnedColumns[KNOWLEDGE_GRAPH_TAB]?.includes(id)
  );

  const orderingMatches = tableState.columnOrdering[KNOWLEDGE_GRAPH_TAB]?.every(
    id =>
      savedScreenTableState.columnOrdering[KNOWLEDGE_GRAPH_TAB]?.includes(id)
  );

  return {
    sorting: sortingMatches
      ? tableState.sorting
      : savedScreenTableState.sorting,
    pinnedColumns: pinnedMatches
      ? tableState.pinnedColumns
      : savedScreenTableState.pinnedColumns,
    columnOrdering: orderingMatches
      ? tableState.columnOrdering
      : savedScreenTableState.columnOrdering,
  };
};

export const clearActiveFilterOptions = (
  filterGroups: FilterGroups<ScreenerFilterGroupKeys>
): FilterGroups<ScreenerFilterGroupKeys> => ({
  [MARKET_FILTERS]: {
    ...filterGroups[MARKET_FILTERS],
    activeFilterOptions: [],
  },
  [KNOWLEDGE_GRAPH_FILTERS]: {
    ...filterGroups[KNOWLEDGE_GRAPH_FILTERS],
    activeFilterOptions: [],
  },
});

export const isFilterWithChildOptionActive = (filterOptions: FilterOption[]) =>
  filterOptions.some(o => !o.childName);

// The filter addition only occurs up to the count of available options
// unless filter is searchable or has custom option
export const showMultiUseFilter = (
  filter: ScreenerFilter,
  activeFilterOptions: ActiveFilterOptions[]
) => {
  if (filter.searchable || filter.config.custom) {
    return true;
  }

  const isAllSelected =
    !!filter.config.options.length &&
    activeFilterOptions.length === filter.config.options.length;
  return !isAllSelected;
};

export const getParentFilterOptions = ({
  filter,
  filterOptions,
  currentFilterOptions,
}: {
  filter: ScreenerFilter;
  currentFilterOptions: ActiveFilterOptions[];
  filterOptions: FilterOption[];
}) => {
  if (!filter.dynamic) {
    return filterOptions;
  }

  const currentOptions =
    filter.config.children &&
    currentFilterOptions.find(f => f.id === filter.key)?.options;
  return currentOptions || [];
};

// this is temporary logic
// see https://toggleglobal.slack.com/archives/C070NQ91AH1/p1726556414456699
export const getParentFilterDisabledOptions = (
  filter: ScreenerFilter,
  filterOptions: FilterOption[]
) => {
  return filter.dynamic && !filter.config.children ? filterOptions : [];
};
